/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import React from "react";
import ReactDOM, { render } from "react-dom";

import { IModelApp, IModelConnection, ScreenViewport, ToolSettings } from "@itwin/core-frontend";

import { cRealityData } from "./RealityData";
import { cRealityDataApi } from "./RealityDataApi";
import { cViewClip } from "./ViewClip";
import { cView } from "./View";
import { cSelection } from "./Selection";
import { cQuery } from "./Query";
import { cClassifier } from "./Classifier";
import { cPlacementTool } from "./PlacementTool";
import { cMarker } from "./Marker";
import { cIssue } from "./Issues";
import { cLabel } from "./Label";
import { IssuesWidget } from "./IssuesUi";
import { NavigationWidget } from "./NavigationWidget";
import { cToolbars } from "./Toolbars";
import { cStatusBar } from "./StatusBar";
import { cSearchWidget } from "./SearchWidget";
import { cIndex } from "./RMIndex";
import { cMessage } from "./Message";
import { cSelectionPopup } from "./SelectionPopup";
import { cPropertyWidget } from "./PropertyWidget";
import { cDatabaseApi } from "./DatabaseApi";
import { cPropertiesApi } from "./PropertiesApi";
import { cUserManager } from "./UserManager";
import { cCrossProbing } from "./CrossProbing";
import { cMultiViewport } from "./MultiViewport";

import App from "../App";
import InitApp from "../InitApp";

export class cInitApp {
  public static uiProviders = [
    new cToolbars(),
    new cStatusBar(),
    new cSearchWidget(),
    new NavigationWidget(),
    new IssuesWidget(),
    new cPropertyWidget(),
  ];

  public static async setupNamespace(namespaceName: string): Promise<string> {
    console.log(namespaceName);
    const sampleNamespace = await IModelApp.localization.registerNamespace(
      namespaceName
    );
    console.log(sampleNamespace);
    return sampleNamespace as unknown as string;
  }

  public static async setupViewer(vp: ScreenViewport, imodel: IModelConnection) {
    // query and database
    console.log("setupViewer:cQuery.initQuery");
    cQuery.initQuery(vp, imodel);

    const proj = process.env.IMJS_DATABASE_NAME!;
    cDatabaseApi.initDatabaseApi(proj);

    // messaging
    cMessage.initMessage();
    cMessage.showStatusBarMessage("Initializing, please wait...");

    // setup views
    console.log("setupViewer:cView.initView");
    await cView.initView(vp, imodel);
    
    //!!OA should use await - see comment in multiviewport.tsx
    cMultiViewport.initFrontstage(imodel, vp);

    await cUserManager.initializeUser();

    console.log("setupViewer:cViewClip.initClipping");
    cViewClip.initClipping(vp, imodel);

    // setup reality model and index model
    const rm = process.env.IMJS_REALITY_MODEL;
    console.log("setupViewer:cRealityData");
    cRealityData.initRealityModel(vp, imodel);
    await cRealityData.loadRealityModels();

    console.log("setupViewer:cClassifier.initClassifier");
    cClassifier.initClassifier(vp, imodel);

    console.log("setupViewer:Index");
    cIndex.initIndex(vp, imodel, rm!);
    
    // maker placement
    console.log("setupViewer:registerNamespace");
    //AH
    const sampleNamespace = await this.setupNamespace(
      "marker-pin-i18n-namespace"
    );
    //console.log(sampleNamespace);
    //AH
    console.log("setupViewer:cPlacementTool");
    cPlacementTool.register("marker-pin-i18n-namespace"); // !!OA ???  Why is this needed
    console.log("setupViewer:cMarker");
    cMarker.initMarkerPin(vp, imodel);

    // selection
    console.log("setupViewer:cSelection.initSelection");
    cSelection.initSelection(vp, imodel);
    // selection popup toolbar
    cSelectionPopup.initPopupToolbar(vp, imodel);

    // classifier - reality model must be initialized prior to setting classifiers
    console.log("setupViewer:cClassifier.initClassifier");
    cClassifier.initClassifier(vp, imodel);

    // issues and labels
    console.log("setupViewer:cIssues");
    cIssue.initIssue(vp, imodel);
    cLabel.initLabel(vp, imodel);

    // metric units
    console.log("quantityFormatter");
    IModelApp.quantityFormatter.setActiveUnitSystem(false);

    // toolbar style
    console.log("toolbarStyle");
    cToolbars.setToolbarStyle();

    cPropertiesApi.initProperties("Element");

//    await cMultiViewport.initFrontstage(imodel, vp);
    await cCrossProbing.initCrossProbing(imodel, vp);

    ToolSettings.wheelZoomRatio = 1.3; // Zoom Scroll Speed using Mouse

    return true;

  }

  public static async reInitApp() {
    // cRealityData.emptyRealityModels();
    // cRealityDataApi.emptyRealityData();
    // console.log("Removed")
    // ReactDOM.render(
    //   <React.StrictMode>
    //   <App />
    // </React.StrictMode>,
    // document.getElementById("root")
    // )
    cMessage.raiseUiEvent("reset-geminai");
  }

  public static async logout() {
    window.location.reload();
  }
}